import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const ProtectedRoute = ({
  children,
  requiresRole = null,
  requiredPermission = null, // Nuevo: permiso específico requerido para acceder
  requiresOnboardingComplete = true,
  allowedForNewUsers = false
}) => {
  const {
    currentUser,
    customClaims,
    loading,
    isNewUser,
    needsOnboarding,
    verifyRestaurantAccess,
    restaurantId: userRestaurantId,
    isOwner,
    isSuperAdmin,
    isGroupAdmin,
    isRestaurantSuspended
  } = useAuth();
  
  // Obtener permisos del usuario (retrocompatible con diferentes estructuras de claims)
  const userPermissions = customClaims?.staffData?.permissions || 
                          customClaims?.permissions || 
                          {};

  // Mapa de permisos a rutas para redirección inteligente
  const permissionRoutes = [
    { permission: 'dashboard', path: '' },
    { permission: 'customerExperience', path: '/customer-experience' },
    { permission: 'accountManagement', path: '/account-management' },
    { permission: 'staffAmplifier', path: '/staff-amplifier' },
    { permission: 'kitchenMatrix', path: '/kitchen-matrix' },
    { permission: 'mixologyLab', path: '/mixology-lab' },
    { permission: 'clients', path: '/clients' },
    { permission: 'reviews', path: '/reviews' },
    { permission: 'rewards', path: '/rewards' },
    { permission: 'billing', path: '/billing' },
    { permission: 'tableManagement', path: '/table-management' },
    { permission: 'integrations', path: '/integrations' },
    { permission: 'settings', path: '/restaurant-config' },
    { permission: 'collaborators', path: '/collaborators' },
    { permission: 'marketing', path: '/marketing' },
  ];

  // Encontrar la primera sección a la que el usuario tiene acceso
  const findFirstAllowedRoute = (restaurantId) => {
    for (const route of permissionRoutes) {
      if (isOwner || isGroupAdmin || userPermissions[route.permission] === true) {
        return `/${restaurantId}${route.path}`;
      }
    }
    return '/'; // Fallback si no tiene ningún permiso
  };

  const { restaurantId: urlRestaurantId } = useParams();

  // Mostrar loading mientras se carga el estado de auth
  if (loading) {
    return (
      <div className="min-h-screen bg-surface-app flex items-center justify-center">
        <div className="kumia-spinner">
          <div className="spinner-ring"></div>
          <div className="spinner-text">
            <span className="text-content-primary">Kum</span>
            <span className="text-kumia-green">IA</span>
          </div>
        </div>
      </div>
    );
  }

  // Verificar autenticación
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // ✨ VERIFICAR EMAIL VERIFICADO ✨
  // Permitir acceso a /verify-email sin verificación
  if (!currentUser.emailVerified && window.location.pathname !== '/verify-email') {
    console.log('⚠️ Email no verificado, redirigiendo a /verify-email');
    return <Navigate to="/verify-email" replace />;
  }

  // Permitir acceso a usuarios nuevos de Google en rutas específicas
  if (isNewUser && allowedForNewUsers) {
    return children;
  }

  // Verificar que tenga Custom Claims (pueden tardar en cargar)
  if (!customClaims) {
    return (
      <div className="min-h-screen bg-surface-app flex items-center justify-center">
        <div className="text-center text-content-primary">
          <div className="kumia-spinner mb-4">
            <div className="spinner-ring"></div>
          </div>
          <p>Verificando permisos...</p>
        </div>
      </div>
    );
  }

  // Verificar rol requerido (compatible con estructura antigua y nueva de claims)
  const hasRequiredRole = !requiresRole || 
    customClaims.role === requiresRole || 
    customClaims.roles?.includes(requiresRole);
  
  if (requiresRole && !hasRequiredRole) {
    toast.error(`🚫 Acceso denegado: Se requiere rol ${requiresRole}`);
    return <Navigate to="/unauthorized" replace />;
  }

  // Verificar si necesita completar onboarding
  // EXCEPCIONES: Si acabamos de completar el onboarding o estamos creando perfil de Google, no redirigir
  const justCompletedOnboarding = sessionStorage.getItem('onboardingJustCompleted') === 'true';
  const creatingGoogleProfile = sessionStorage.getItem('creatingGoogleProfile') === 'true';

  if (requiresOnboardingComplete && needsOnboarding && !justCompletedOnboarding && !creatingGoogleProfile) {
    return <Navigate to="/onboarding" replace />;
  }

  // Si el restaurante está suspendido, redirigir a página de suspensión
  if (isRestaurantSuspended) {
    return <Navigate to="/suspended" replace />;
  }

  // Verificar acceso específico al restaurante (si hay restaurantId en URL)
  if (urlRestaurantId && !verifyRestaurantAccess(urlRestaurantId)) {
    // Mostrar toast y redirigir inmediatamente sin mostrar pantalla intermedia
    toast.error('🚫 Acceso denegado a este restaurante', {
      id: 'access-denied', // Evitar duplicados
    });

    // Redirigir inmediatamente al dashboard del usuario correcto
    if (userRestaurantId) {
      // Nueva estructura: /:restaurantId (sin /dashboard)
      return <Navigate to={`/${userRestaurantId}`} replace />;
    } else {
      return <Navigate to="/" replace />;
    }
  }

  // Verificar permiso específico para la sección (si se requiere)
  if (requiredPermission) {
    // group_owner pasa por isOwner; group_admin tiene acceso owner-equivalente en
    // las sedes de su scope (verifyRestaurantAccess ya validó la sede). Ola 1.
    const hasPermission = isOwner || isSuperAdmin || isGroupAdmin || userPermissions[requiredPermission] === true;
    
    if (!hasPermission) {
      console.log('🚫 Acceso denegado - Permiso requerido:', requiredPermission, {
        isOwner,
        userPermissions,
        hasPermission
      });
      
      // Encontrar la primera sección permitida y redirigir ahí
      const restaurantId = urlRestaurantId || userRestaurantId;
      const redirectTo = restaurantId ? findFirstAllowedRoute(restaurantId) : '/';
      
      toast.error('🚫 No tienes permisos para acceder a esta sección', {
        id: 'permission-denied',
      });

      return <Navigate to={redirectTo} replace />;
    }
  }

  // Si pasa todas las verificaciones, renderizar children
  return children;
};

export default ProtectedRoute;

